import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, ChevronLeft, MapPin, Sparkles, Flame, Zap, 
  Mountain, Compass, Palmtree, Calendar, DollarSign, Check,
  Loader2, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanMyTrip = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    region: null,
    vibe: null,
    burnLevel: null,
    travelMonth: null,
    budget: null
  });
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const burnIcons = { 1: Sparkles, 2: Flame, 3: Zap };
  const burnColors = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };
  const burnBgColors = {
    1: 'bg-purple-600 text-white',
    2: 'bg-orange-500 text-white',
    3: 'bg-red-600 text-white'
  };

  const steps = [
    {
      id: 'region',
      question: language === 'es' ? '¿A dónde quieres ir?' : 'Where do you want to go?',
      subtitle: language === 'es' ? 'Elige tu región preferida' : 'Choose your preferred region',
      options: [
        { 
          value: 'north_central_america', 
          label: language === 'es' ? 'Norte y Centroamérica' : 'North & Central America',
          description: language === 'es' ? 'México, Costa Rica, Guatemala' : 'Mexico, Costa Rica, Guatemala',
          icon: Mountain,
          color: 'from-emerald-500 to-teal-600'
        },
        { 
          value: 'south_america', 
          label: language === 'es' ? 'Sudamérica' : 'South America',
          description: language === 'es' ? 'Perú, Brasil, Patagonia' : 'Peru, Brazil, Patagonia',
          icon: Compass,
          color: 'from-orange-500 to-amber-600'
        },
        { 
          value: 'caribbean', 
          label: language === 'es' ? 'Caribe' : 'Caribbean',
          description: language === 'es' ? 'República Dominicana, Cuba' : 'Dominican Republic, Cuba',
          icon: Palmtree,
          color: 'from-cyan-500 to-blue-600'
        },
        { 
          value: 'any', 
          label: language === 'es' ? 'Sorpréndeme' : 'Surprise me!',
          description: language === 'es' ? 'Estoy abierto a cualquier destino' : "I'm open to any destination",
          icon: Sparkles,
          color: 'from-[#84bc00] to-emerald-600'
        }
      ]
    },
    {
      id: 'vibe',
      question: language === 'es' ? '¿Qué tipo de experiencia buscas?' : 'What kind of experience are you looking for?',
      subtitle: language === 'es' ? 'Elige tu vibra' : 'Choose your vibe',
      options: [
        { 
          value: 'adventure', 
          label: language === 'es' ? 'Aventura' : 'Adventure',
          description: language === 'es' ? 'Senderismo, rafting, adrenalina' : 'Hiking, rafting, adrenaline',
          emoji: '🏔️',
          color: 'from-orange-500 to-red-600'
        },
        { 
          value: 'culture', 
          label: language === 'es' ? 'Cultura' : 'Culture',
          description: language === 'es' ? 'Historia, gastronomía, tradiciones' : 'History, gastronomy, traditions',
          emoji: '🎭',
          color: 'from-purple-500 to-indigo-600'
        },
        { 
          value: 'relaxation', 
          label: language === 'es' ? 'Relajación' : 'Relaxation',
          description: language === 'es' ? 'Playas, spa, descanso' : 'Beaches, spa, rest',
          emoji: '🌴',
          color: 'from-cyan-500 to-teal-600'
        },
        { 
          value: 'any', 
          label: language === 'es' ? 'Un poco de todo' : 'A bit of everything',
          description: language === 'es' ? 'Mezcla de experiencias' : 'Mix of experiences',
          emoji: '✨',
          color: 'from-[#84bc00] to-emerald-600'
        }
      ]
    },
    {
      id: 'burnLevel',
      question: language === 'es' ? '¿Cuánta intensidad buscas?' : 'How intense do you want it?',
      subtitle: language === 'es' ? 'Elige tu nivel de intensidad' : 'Choose your intensity level',
      options: [
        { 
          value: 1, 
          label: language === 'es' ? 'El Socialité' : 'The Socialite',
          description: language === 'es' ? '2-4 hrs/día • Cultura, cócteles, aventura suave' : '2-4 hrs/day • Culture, cocktails, soft adventure',
          icon: Sparkles,
          color: 'from-purple-500 to-pink-500'
        },
        { 
          value: 2, 
          label: language === 'es' ? 'La Vibra' : 'The Vibe',
          description: language === 'es' ? '4-6 hrs/día • Volcanes, surf, inmersión' : '4-6 hrs/day • Volcanoes, surf, immersion',
          icon: Flame,
          color: 'from-orange-500 to-amber-500'
        },
        { 
          value: 3, 
          label: language === 'es' ? 'El Temerario' : 'The Badass',
          description: language === 'es' ? '6-8+ hrs/día • Sin señal, sin atajos' : '6-8+ hrs/day • No signal, no shortcuts',
          icon: Zap,
          color: 'from-red-500 to-orange-600'
        },
        { 
          value: 'any', 
          label: language === 'es' ? 'Cualquier nivel' : 'Any level',
          description: language === 'es' ? 'Estoy abierto a todo' : "I'm open to anything",
          icon: Check,
          color: 'from-[#84bc00] to-emerald-600'
        }
      ]
    },
    {
      id: 'travelMonth',
      question: language === 'es' ? '¿Cuándo quieres viajar?' : 'When do you want to travel?',
      subtitle: language === 'es' ? 'Elige tu mes preferido' : 'Choose your preferred month',
      options: [
        { value: 'apr-may', label: language === 'es' ? 'Abril - Mayo 2026' : 'April - May 2026', emoji: '🌸' },
        { value: 'jun-jul', label: language === 'es' ? 'Junio - Julio 2026' : 'June - July 2026', emoji: '☀️' },
        { value: 'aug-sep', label: language === 'es' ? 'Agosto - Septiembre 2026' : 'August - September 2026', emoji: '🍂' },
        { value: 'oct-dec', label: language === 'es' ? 'Octubre - Diciembre 2026' : 'October - December 2026', emoji: '❄️' },
        { value: 'flexible', label: language === 'es' ? 'Soy flexible' : "I'm flexible", emoji: '📅' }
      ]
    },
    {
      id: 'budget',
      question: language === 'es' ? '¿Cuál es tu presupuesto?' : "What's your budget?",
      subtitle: language === 'es' ? 'Por persona, todo incluido' : 'Per person, all-inclusive',
      options: [
        { value: 'budget', label: language === 'es' ? 'Menos de $1,500' : 'Under $1,500', emoji: '💵' },
        { value: 'mid', label: '$1,500 - $2,000', emoji: '💰' },
        { value: 'premium', label: '$2,000 - $3,000', emoji: '💎' },
        { value: 'any', label: language === 'es' ? 'Sin límite' : 'No limit', emoji: '🌟' }
      ]
    }
  ];

  const handleSelect = (value) => {
    const stepId = steps[currentStep].id;
    setAnswers(prev => ({ ...prev, [stepId]: value }));
    
    // Auto-advance to next step after selection
    setTimeout(() => {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Last step - fetch recommendations
        fetchRecommendations({ ...answers, [stepId]: value });
      }
    }, 300);
  };

  const fetchRecommendations = async (finalAnswers) => {
    setLoading(true);
    try {
      // Build query params based on answers
      const params = new URLSearchParams();
      params.append('lang', language);
      
      if (finalAnswers.region && finalAnswers.region !== 'any') {
        params.append('region', finalAnswers.region);
      }
      if (finalAnswers.vibe && finalAnswers.vibe !== 'any') {
        params.append('vibe', finalAnswers.vibe);
      }
      if (finalAnswers.burnLevel && finalAnswers.burnLevel !== 'any') {
        params.append('burn_level', finalAnswers.burnLevel);
      }

      const response = await fetch(`${API_URL}/api/trips?${params.toString()}`);
      const data = await response.json();
      
      let trips = data.trips || [];
      
      // Filter by budget if specified
      if (finalAnswers.budget && finalAnswers.budget !== 'any') {
        const budgetRanges = {
          'budget': { max: 1500 },
          'mid': { min: 1500, max: 2000 },
          'premium': { min: 2000, max: 3000 }
        };
        const range = budgetRanges[finalAnswers.budget];
        if (range) {
          trips = trips.filter(trip => {
            if (range.min && trip.price < range.min) return false;
            if (range.max && trip.price > range.max) return false;
            return true;
          });
        }
      }

      setRecommendations(trips);
      setShowResults(true);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const restart = () => {
    setCurrentStep(0);
    setAnswers({
      region: null,
      vibe: null,
      burnLevel: null,
      travelMonth: null,
      budget: null
    });
    setRecommendations([]);
    setShowResults(false);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#84bc00] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            {language === 'es' ? 'Encontrando tu viaje perfecto...' : 'Finding your perfect trip...'}
          </h2>
          <p className="text-gray-400">
            {language === 'es' ? 'Esto solo tomará un momento' : 'This will only take a moment'}
          </p>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20" data-testid="plan-my-trip-results">
        {/* Results Header */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-[#84bc00]/20 text-[#84bc00] text-sm font-bold rounded-full mb-4">
              {recommendations.length > 0 
                ? (language === 'es' ? `${recommendations.length} VIAJES ENCONTRADOS` : `${recommendations.length} TRIPS FOUND`)
                : (language === 'es' ? 'RESULTADOS' : 'RESULTS')
              }
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              {recommendations.length > 0 
                ? (language === 'es' ? '¡Aquí están tus viajes!' : 'Here are your trips!')
                : (language === 'es' ? 'No encontramos coincidencias exactas' : "We couldn't find exact matches")
              }
            </h1>
            <p className="text-gray-300 mb-6">
              {recommendations.length > 0 
                ? (language === 'es' ? 'Basado en tus preferencias, estos son los viajes perfectos para ti' : 'Based on your preferences, these are the perfect trips for you')
                : (language === 'es' ? 'Intenta ajustar tus criterios o explora todos nuestros viajes' : 'Try adjusting your criteria or explore all our trips')
              }
            </p>
            <Button
              onClick={restart}
              variant="outline"
              className="border-white/50 text-white hover:bg-white hover:text-black"
            >
              {language === 'es' ? 'Empezar de nuevo' : 'Start over'}
            </Button>
          </div>
        </div>

        {/* Results Grid */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {recommendations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recommendations.map((trip) => {
                const BurnIcon = burnIcons[trip.burn_level] || Flame;
                const burnGradient = burnColors[trip.burn_level] || burnColors[2];
                const burnBg = burnBgColors[trip.burn_level] || burnBgColors[2];

                return (
                  <div
                    key={trip.id}
                    data-testid={`recommendation-${trip.id}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  >
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={trip.image}
                        alt={trip.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      {/* Burn Level Badge */}
                      <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full ${burnBg} flex items-center gap-1.5`}>
                        <BurnIcon className="w-4 h-4" />
                        <span className="text-xs font-bold">{trip.burn_label}</span>
                      </div>
                      {/* Price */}
                      <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg">
                        <span className="text-xs text-gray-300">{language === 'es' ? 'desde' : 'from'}</span>
                        <span className="text-lg font-black ml-1">${trip.price?.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span>{trip.location}</span>
                        <span>•</span>
                        <span>{trip.duration}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#84bc00] transition-colors">
                        {trip.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {trip.description}
                      </p>
                      <Button
                        onClick={() => navigate(`/trips/${trip.id}`)}
                        className={`w-full bg-gradient-to-r ${burnGradient} hover:opacity-90 text-white font-bold`}
                      >
                        {language === 'es' ? 'Ver Detalles' : 'View Details'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Button
                onClick={() => navigate('/trips')}
                className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold px-8 py-4"
              >
                {language === 'es' ? 'Explorar Todos los Viajes' : 'Explore All Trips'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20" data-testid="plan-my-trip-wizard">
      {/* Progress Bar */}
      <div className="fixed top-20 left-0 right-0 h-1 bg-gray-700 z-40">
        <div 
          className="h-full bg-[#84bc00] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Step Counter */}
        <div className="text-center mb-8">
          <span className="text-[#84bc00] font-bold">
            {language === 'es' ? 'PASO' : 'STEP'} {currentStep + 1} / {steps.length}
          </span>
        </div>

        {/* Question */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {currentStepData.question}
          </h1>
          <p className="text-gray-400 text-lg">
            {currentStepData.subtitle}
          </p>
        </div>

        {/* Options */}
        <div className={`grid gap-4 ${currentStepData.options.length <= 4 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
          {currentStepData.options.map((option, index) => {
            const isSelected = answers[currentStepData.id] === option.value;
            const IconComponent = option.icon;
            
            return (
              <button
                key={option.value}
                data-testid={`option-${currentStepData.id}-${option.value}`}
                onClick={() => handleSelect(option.value)}
                className={`
                  relative p-6 rounded-2xl text-left transition-all duration-300 
                  ${isSelected 
                    ? 'bg-[#84bc00] text-black scale-105 shadow-xl shadow-[#84bc00]/30' 
                    : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-[1.02]'
                  }
                `}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Icon or Emoji */}
                <div className="mb-4">
                  {IconComponent ? (
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center
                      ${isSelected ? 'bg-black/20' : `bg-gradient-to-r ${option.color}`}
                    `}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                  ) : option.emoji ? (
                    <span className="text-4xl">{option.emoji}</span>
                  ) : null}
                </div>

                {/* Label */}
                <h3 className={`text-xl font-bold mb-2 ${isSelected ? 'text-black' : 'text-white'}`}>
                  {option.label}
                </h3>

                {/* Description */}
                {option.description && (
                  <p className={`text-sm ${isSelected ? 'text-black/70' : 'text-gray-400'}`}>
                    {option.description}
                  </p>
                )}

                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-black rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#84bc00]" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-12">
          <Button
            onClick={goBack}
            variant="ghost"
            disabled={currentStep === 0}
            className={`text-white hover:text-[#84bc00] ${currentStep === 0 ? 'opacity-0' : ''}`}
            data-testid="wizard-back-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {language === 'es' ? 'Atrás' : 'Back'}
          </Button>

          <Button
            onClick={() => navigate('/trips')}
            variant="ghost"
            className="text-gray-500 hover:text-white"
          >
            {language === 'es' ? 'Saltar y ver todos' : 'Skip and view all'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlanMyTrip;
